import { useMutation } from '@tanstack/react-query';
import { SendIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// biome-ignore lint/style/noRestrictedImports: colocated mutation for system-level invite, mirrors invite-email-form.
import { systemInvite as baseSystemInvite } from 'sdk';
import { useDialoger } from '~/modules/common/dialoger/use-dialoger';
import { SelectRoleRadio } from '~/modules/common/form-fields/select-role-radio';
import { toaster } from '~/modules/common/toaster/toaster';
import type { EnrichedChannel } from '~/modules/entities/types';
import { useInviteMemberMutation } from '~/modules/memberships/query-mutations';
import { Badge } from '~/modules/ui/badge';
import { Button, SubmitButton } from '~/modules/ui/button';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '~/modules/ui/field';
import { Textarea } from '~/modules/ui/textarea';
import { type InviteFormValues, useInviteFormDraft } from '~/modules/user/invite-users';

/** Extract unique, lowercased email addresses from any pasted text (commas, newlines, address-book dumps). */
export const extractEmails = (text: string): string[] => {
  const matches = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) ?? [];
  return [...new Set(matches.map((email) => email.toLowerCase()))];
};

interface Props {
  channel?: EnrichedChannel;
  dialog?: boolean;
  children?: React.ReactNode;
}

export function InviteBulkEmailForm({ channel, dialog: isDialog, children }: Props) {
  const { t } = useTranslation();

  const [rawText, setRawText] = useState('');
  const form = useInviteFormDraft(channel?.id, channel?.entityType);

  const onTextChange = (text: string) => {
    setRawText(text);
    form.setValue('emails', extractEmails(text), { shouldDirty: true });
  };

  const emails = form.getValues('emails') ?? [];

  const onSuccess = (
    { invitesSentCount, rejectedIds }: { rejectedIds: string[]; invitesSentCount: number },
    submittedEmails: string[],
  ) => {
    form.reset();
    setRawText('');
    if (isDialog) useDialoger.getState().remove();

    if (invitesSentCount > 0) {
      const resource = t('c:user', { count: invitesSentCount }).toLowerCase();
      toaster.success(t('c:success.resource_count_invited', { count: invitesSentCount, resource }));
    }
    if (rejectedIds.length)
      toaster.info(t('c:still_not_accepted', { count: rejectedIds.length, total: submittedEmails.length }));
  };

  const { mutate: membershipInvite, isPending } = useInviteMemberMutation();
  const { mutate: systemInvite, isPending: isSystemInvitePending } = useMutation({
    mutationFn: (body: InviteFormValues) => baseSystemInvite({ body }),
    onSuccess: (result, body) => onSuccess(result, body.emails),
  });

  const onSubmit = (body: InviteFormValues) => {
    if (!channel) return systemInvite(body);

    const organizationId = channel.organizationId || channel.id;
    const path = { tenantId: channel.tenantId, organizationId: organizationId };
    const query = { entityId: channel.id, entityType: channel.entityType };

    membershipInvite({ body, path, query, channel }, { onSuccess: (result) => onSuccess(result, body.emails) });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormItem name="bulk-emails">
          <FormLabel help={t('c:paste_emails.text')}>{t('c:paste_emails')}</FormLabel>
          <Textarea
            value={rawText}
            onChange={(event) => onTextChange(event.target.value)}
            placeholder={t('c:paste_emails.placeholder')}
            autoResize
            className="min-h-24"
            autoComplete="off"
          />
          <div className="text-muted-foreground text-sm">{t('c:emails_recognized', { count: emails.length })}</div>
          <FormField control={form.control} name="emails" render={() => <FormMessage />} />
        </FormItem>

        {channel && (
          <FormField
            control={form.control}
            name="role"
            render={({ field: { value, onChange } }) => (
              <FormItem className="ml-3 flex-row items-center gap-4">
                <FormLabel>{t('c:role')}</FormLabel>
                <SelectRoleRadio value={value} onValueChange={onChange} entityType={channel.entityType} />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <SubmitButton disabled={!emails.length} loading={isPending || isSystemInvitePending} className="relative">
            {!!emails.length && (
              <Badge variant="secondary" context="button">
                {emails.length}
              </Badge>
            )}{' '}
            <SendIcon className="mr-2" />
            {t('c:invite')}
          </SubmitButton>
          {children}

          {!children && form.isDirty && (
            <Button
              type="reset"
              variant="secondary"
              onClick={() => {
                form.reset();
                setRawText('');
              }}
            >
              {t('c:cancel')}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
